/* ===========================================================
   Quill 본문 뷰어 (읽기전용)
   - 기사 상세 content(Quill Delta)를 관리자 에디터와 동일한 Quill 스타일로 렌더
   - Quill 2.x 코어 사용 (react-quill은 React 19에서 findDOMNode 이슈로 미사용)
   - size 가 숫자(px)로 저장된 Delta를 위해 style 기반 attributor 를 허용 등록
   =========================================================== */
import { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

/* size/align/font 를 인라인 스타일 기반으로 등록(임의 값 허용)
   → 관리자 에디터가 저장한 size:10 같은 px 값도 그대로 반영 */
let registered = false;
function ensureRegistered() {
  if (registered) return;
  try {
    const SizeStyle = Quill.import("attributors/style/size");
    SizeStyle.whitelist = null; // 임의 size 허용
    const AlignStyle = Quill.import("attributors/style/align");
    const FontStyle = Quill.import("attributors/style/font");
    FontStyle.whitelist = null;
    Quill.register(SizeStyle, true);
    Quill.register(AlignStyle, true);
    Quill.register(FontStyle, true);
  } catch (e) {
    console.error("Quill attributor register error:", e);
  }
  registered = true;
}

/* 유튜브 주소 → 영상 ID (watch · youtu.be · embed · live · shorts)
   ★ 같은 판정을 기자 BO 와 앱도 한다. 한 곳만 고치면 「BO 에서는 넣었는데
     여기서는 안 나온다」가 된다:
       BO  journalist/lib/commons/widgets/components/quill_editor_toolbar_widget.dart
       앱  donga_front/lib/common/widgets/components/article_youtube_player.dart */
const YOUTUBE_RE =
  /^(?:https?:\/\/)?(?:www\.|m\.|music\.)?(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|live\/|shorts\/|v\/)|youtu\.be\/)([_\-a-zA-Z0-9]{11})/;

function toYoutubeEmbedUrl(url) {
  if (typeof url !== "string") return null;
  const m = url.trim().match(YOUTUBE_RE);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

/* Delta ops 정규화
   ① size 가 숫자면 "Npx" 로 변환 (style attributor가 적용되도록)
   ② video op 의 유튜브 주소를 embed 주소로 변환

   ★ ②가 필요한 이유 — 기자 BO(flutter_quill)는 «원본 주소 그대로» 저장한다
     ({"insert":{"video":"https://www.youtube.com/watch?v=..."}}).
     Quill 2 의 video blot 은 그 값을 iframe src 에 그대로 넣는데,
     유튜브는 watch 주소의 iframe 삽입을 «거부»해서 빈 화면이 된다.
     embed/{id} 로 바꿔야 재생된다. 유튜브가 아닌 주소는 손대지 않는다. */
/* 영상의 폭·정렬을 «문서 순서대로» 모은다.

   ★ 왜 렌더 «뒤»에 얹는가 — Quill 2 의 video blot 은 Delta 의 임의 속성을
     DOM 으로 옮겨 주지 않는다(등록된 attributor 만 옮긴다). embed 용 attributor 를
     새로 만드는 것보다, 만들어진 iframe 에 순서대로 스타일을 얹는 편이
     짧고 깨질 곳이 적다. ops 의 video 순서와 DOM 의 iframe.ql-video 순서는 «같다».

   ★ 규약은 기자 BO·앱과 공유한다. 형식을 바꾸면 «셋 다» 바꿔야 한다:
       BO  journalist/lib/commons/widgets/components/article_video_layout.dart
       앱  donga_front/lib/common/widgets/components/article_youtube_player.dart
     속성이 없으면 폭 100% · 왼쪽 — 이미 등록된 기사는 모습이 바뀌지 않는다. */
function collectVideoStyles(ops) {
  if (!Array.isArray(ops)) return [];
  return ops
    .filter(
      (op) => op && op.insert && typeof op.insert === "object" && op.insert.video
    )
    .map((op) => (op.attributes && op.attributes.style) || "");
}

/* 모은 스타일을 iframe 에 얹는다.
   ★ width 와 margin «둘만» 받고 값도 미리 정한 것만 통과시킨다 —
     기사 본문에서 임의 CSS 가 들어오는 길을 열어 두지 않는다. */
function applyVideoStyles(host, styles) {
  host.querySelectorAll("iframe.ql-video").forEach((frame, i) => {
    const decls = {};
    String(styles[i] || "")
      .split(";")
      .forEach((part) => {
        const at = part.indexOf(":");
        if (at <= 0) return;
        decls[part.slice(0, at).trim().toLowerCase()] = part
          .slice(at + 1)
          .trim();
      });

    // 10~100 으로 가둔다 — Flutter 쪽(BO·앱)의 clamp(10, 100) 와 같아야 한다.
    const pct = /^(\d{1,3})%$/.exec(decls.width || "");
    const width = pct ? `${Math.min(100, Math.max(10, Number(pct[1])))}%` : "100%";
    // ★ 위 0 / 아래 14px — «일반 문단과 같은 리듬»이다 (2026-09-17).
    //   위는 앞 문단의 margin-bottom(14px)이 이미 만들어 준다.
    //   아래는 뒤 문단의 margin-top 이 0 이라 영상이 직접 내지 않으면 0px 이 된다.
    //   좌우 auto 는 정렬에 필요하므로 유지한다.
    const margin = decls.margin === "0 auto"
      ? "0 auto 14px"
      : decls.margin === "0 0 0 auto"
        ? "0 0 14px auto"
        : "0 0 14px";

    frame.style.width = width;
    frame.style.maxWidth = "640px";
    frame.style.margin = margin;
  });
}

/* 영상 뒤에 «빈 문단»이 생기는 것을 막는다.

   ★ 원인 — flutter_quill(기자 BO)은 블록 embed 뒤에 줄 종결용 줄바꿈을 함께 저장한다:
       {"insert":{"video":"..."}}, {"insert":"\n사이즈 조절은..."}
     Flutter 쪽은 그 줄바꿈을 «영상 줄의 끝»으로 읽어 아무것도 그리지 않는다.
     그런데 Quill 2(JS)는 블록 embed 가 이미 자기 줄을 차지한다고 보기 때문에
     그 줄바꿈이 «눈에 보이는 빈 문단»(<p> 높이 31px)이 되어 버린다.
     → 앱에서는 멀쩡한데 웹에서만 영상 아래가 한 줄만큼 더 벌어진다. 실제로 그랬다.

   ★ 그래서 영상 «바로 다음» 문자열의 맨 앞 줄바꿈 «하나»만 걷어낸다.
     하나만 지우는 이유: 기자가 «의도적으로» 넣은 빈 줄까지 지우면 안 된다.
     줄바꿈만 있던 op 는 빈 문자열이 되므로 통째로 버린다. */
function dropEmbedTrailingNewline(ops) {
  const out = [];
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    const prev = ops[i - 1];
    const prevIsVideo =
      prev && prev.insert && typeof prev.insert === "object" && prev.insert.video;

    if (prevIsVideo && op && typeof op.insert === "string" && op.insert.startsWith("\n")) {
      const rest = op.insert.slice(1);
      if (rest.length === 0) continue; // 줄바꿈뿐이던 op — 버린다
      out.push({ ...op, insert: rest });
      continue;
    }
    out.push(op);
  }
  return out;
}

function normalizeOps(ops) {
  if (!Array.isArray(ops)) return ops;
  return ops.map((op) => {
    if (!op) return op;

    if (op.insert && typeof op.insert === "object" && op.insert.video) {
      const embedUrl = toYoutubeEmbedUrl(op.insert.video);
      if (embedUrl) {
        return { ...op, insert: { ...op.insert, video: embedUrl } };
      }
      return op;
    }

    if (op.attributes && typeof op.attributes.size === "number") {
      return {
        ...op,
        attributes: { ...op.attributes, size: `${op.attributes.size}px` },
      };
    }
    return op;
  });
}

/**
 * @param {{content: string|Array, className?: string}} props
 *  content: Quill Delta(JSON 문자열 또는 ops 배열) 또는 HTML 문자열
 */
export default function QuillContent({ content, className = "" }) {
  const hostRef = useRef(null);

  useEffect(() => {
    ensureRegistered();
    const host = hostRef.current;
    if (!host) return;

    host.innerHTML = "";
    const el = document.createElement("div");
    host.appendChild(el);

    const quill = new Quill(el, {
      readOnly: true,
      modules: { toolbar: false },
      theme: "snow",
    });

    // content 파싱: Delta(JSON 배열/{ops}) 우선, 실패 시 HTML 로 처리
    let ops = null;
    if (Array.isArray(content)) {
      ops = content;
    } else if (typeof content === "string") {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) ops = parsed;
        else if (parsed && Array.isArray(parsed.ops)) ops = parsed.ops;
      } catch (e) {
        ops = null;
      }
    }

    if (ops) {
      const cleaned = dropEmbedTrailingNewline(ops);
      quill.setContents({ ops: normalizeOps(cleaned) });
      applyVideoStyles(host, collectVideoStyles(cleaned));
    } else if (typeof content === "string" && content) {
      quill.clipboard.dangerouslyPasteHTML(content);
    }

    return () => {
      if (host) host.innerHTML = "";
    };
  }, [content]);

  return <div ref={hostRef} className={`kd-quill ${className}`} />;
}
